import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Zap, Users, Plus, Bell, Play, MapPin, ChevronRight } from 'lucide-react';
import { Sheet, plural } from './AdminUi';

// Aktiviraj intervenciju: panel s brzim odabirom vrste (upiše početak naziva), prvom lokacijom
// „u tijeku odmah” i potvrdom povlačenjem (obavijest + push idu svim članovima — bez slučajnog slanja).

const TYPES = ['Požar', 'Stablo / vjetar', 'Poplava', 'Tehnička', 'Prometna'];
const SEP = ' — ';
const typeOf = (title) => TYPES.find(t => title.startsWith(t + SEP) || title === t) || null;

// Klizač „Povuci za aktivaciju”: potvrda tek kad ručica stigne do kraja; tipkovnica: Enter / razmak.
function SlideToConfirm({ label, disabledLabel, disabled, busy, onConfirm }) {
  const trackRef = useRef(null);
  const drag = useRef({ active: false, x0: 0, id: 0 });
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const KNOB = 52, PAD = 5;
  const maxX = () => Math.max(0, (trackRef.current?.clientWidth || 0) - KNOB - PAD * 2);

  useEffect(() => { if (!busy) setX(0); }, [busy]);

  const onDown = (e) => {
    if (disabled || busy) return;
    drag.current = { active: true, x0: e.clientX - x, id: e.pointerId };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    setDragging(true);
  };
  const onMove = (e) => {
    if (!drag.current.active) return;
    setX(Math.min(maxX(), Math.max(0, e.clientX - drag.current.x0)));
  };
  const onUp = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setDragging(false);
    if (x >= maxX() * 0.9) { setX(maxX()); onConfirm(); }
    else setX(0);
  };
  const onKey = (e) => {
    if (disabled || busy) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setX(maxX()); onConfirm(); }
  };

  const pct = maxX() ? x / maxX() : 0;
  return (
    <div ref={trackRef} className={`ai-slide${disabled ? ' off' : ''}`} role="slider" tabIndex={disabled ? -1 : 0}
      aria-label={disabled ? disabledLabel : label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}
      aria-disabled={disabled || busy} onKeyDown={onKey}>
      <span className="ai-slide-fill" style={{ width: x + KNOB + PAD, transition: dragging ? 'none' : undefined }} />
      <span className="ai-slide-txt" style={{ opacity: busy ? 1 : 1 - pct * 1.4 }}>{busy ? 'Aktiviram…' : disabled ? disabledLabel : label}</span>
      <span className="ai-knob" style={{ transform: `translateX(${x}px)`, transition: dragging ? 'none' : undefined }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        {busy ? <span className="ai-spin" /> : <ChevronRight size={22} strokeWidth={2.6} />}
      </span>
    </div>
  );
}

function ActivateInterventionModal({ onClose, onSaved, user }) {
  const [title, setTitle] = useState('');
  const [people, setPeople] = useState(1);
  const [notes, setNotes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [locNow, setLocNow] = useState(true);
  const [address, setAddress] = useState('');
  const [members, setMembers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    api.get('/users').then(r => {
      const me = user?.email;
      setMembers((r.data || []).filter(u => u.is_verified !== false && u.email !== me).length);
    }).catch(() => {});
  }, [user?.email]);

  const current = typeOf(title);
  const pickType = (t) => {
    const rest = current ? title.slice(current.length).replace(/^ — ?/, '') : title;
    setTitle(t + SEP + rest);
    setError('');
    requestAnimationFrame(() => {
      const el = titleRef.current;
      if (el) { el.focus({ preventScroll: true }); el.setSelectionRange(el.value.length, el.value.length); }
    });
  };

  const cleanTitle = title.trim().replace(/\s*—\s*$/, '');
  const dirty = !!(title.trim() || notes.trim() || address.trim());

  const activate = async () => {
    if (!cleanTitle || busy) return;
    setBusy(true); setError('');
    let created;
    try {
      const res = await api.post('/interventions', { title: cleanTitle, notes: notes.trim(), required_people: people });
      created = res.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Aktivacija nije uspjela.');
      setBusy(false);
      return;
    }
    if (locNow && created?.id) {
      try {
        const name = (address.trim() || cleanTitle).slice(0, 100);
        await api.post(`/interventions/${created.id}/locations`, { name, address: address.trim() || null, started_at: new Date().toISOString() });
      } catch {
        alert('Intervencija je aktivirana, ali prva lokacija nije dodana. Dodaj je ručno u intervenciji.');
      }
    }
    onSaved(created);
  };

  return (
    <Sheet tall z={1100} onClose={onClose} dirty={dirty} busy={busy} closeRef={closeRef}
      discardText="Intervencija nije aktivirana. Upisani podaci bit će izgubljeni."
      head={
        <div className="adm-sh">
          <span className="ai-zap"><Zap size={18} fill="currentColor" /></span>
          <div className="adm-main">
            <h3>Aktiviraj intervenciju</h3>
            <p>Kreće odmah · traje 3 h (mijenja se kasnije)</p>
          </div>
        </div>
      }
      footer={
        <div className="adm-dirty">
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <SlideToConfirm label="Povuci za aktivaciju" disabledLabel="Upiši naziv intervencije" disabled={!cleanTitle} busy={busy} onConfirm={activate} />
          <button type="button" className="ai-cancel" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>Odustani</button>
        </div>
      }
    >
      <input id="ai-title" ref={titleRef} className="tf-title" value={title} maxLength={100}
        onChange={e => { setTitle(e.target.value); setError(''); }} placeholder="Što se događa i gdje?" aria-label="Naziv intervencije" />
      <div className="tf-chips">
        {TYPES.map(t => (
          <button key={t} type="button" className={`tf-chip ai-chip${current === t ? ' on' : ''}`} onClick={() => pickType(t)} aria-pressed={current === t}>{t}</button>
        ))}
      </div>

      <div className="adm-card">
        <button type="button" className="tf-row tf-row-btn" onClick={() => setLocNow(v => !v)} aria-pressed={locNow}>
          <span className="tf-ico"><Play size={17} /></span>
          <span className="tf-k">Lokacija u tijeku odmah<small>{locNow ? 'Prva lokacija kreće s aktivacijom' : 'Lokacije dodaješ kasnije u intervenciji'}</small></span>
          <span className={`tf-sw${locNow ? ' on' : ''}`} aria-hidden="true" />
        </button>
        {locNow && (
          <label className="tf-row ai-sub" htmlFor="ai-address">
            <span className="tf-ico"><MapPin size={17} /></span>
            <input id="ai-address" className="ai-inline" value={address} maxLength={100}
              onChange={e => setAddress(e.target.value)} placeholder="Adresa (prazno = naziv intervencije)" />
          </label>
        )}
        <div className="tf-row">
          <span className="tf-ico"><Users size={17} /></span>
          <span className="tf-k">Potrebno ljudi</span>
          <span className="tf-step">
            <button type="button" aria-label="Manje" disabled={people <= 1} onClick={() => setPeople(p => Math.max(1, p - 1))}>−</button>
            <b>{people}</b>
            <button type="button" aria-label="Više" disabled={people >= 50} onClick={() => setPeople(p => Math.min(50, p + 1))}>+</button>
          </span>
        </div>
        {notesOpen ? (
          <div className="tf-row" style={{ alignItems: 'flex-start' }}>
            <textarea id="ai-notes" className="adm-input" rows={3} maxLength={2000} value={notes} autoFocus
              onChange={e => setNotes(e.target.value)} placeholder="Napomena (npr. ulaz iz dvorišta, kontakt na mjestu)" aria-label="Napomena" />
          </div>
        ) : (
          <button type="button" className="tf-row tf-row-btn" onClick={() => setNotesOpen(true)}>
            <span className="tf-ico"><Plus size={17} /></span>
            <span className="tf-k">Dodaj napomenu</span>
          </button>
        )}
      </div>

      <div className="adm-card">
        <div className="tf-row">
          <span className="tf-ico"><Bell size={17} /></span>
          <span className="tf-k">Obavijest i push dobiva<small>Članovi se mogu odmah javiti za sudjelovanje</small></span>
          <b className="ai-count">{members == null ? 'svaki član' : `${members} ${plural(members, 'član', 'člana', 'članova')}`}</b>
        </div>
      </div>
    </Sheet>
  );
}

export default ActivateInterventionModal;
